import { BUNDLE_CATEGORIES, CATEGORIES, categoryLabel, isBundleCategory } from "@/store/constants";
import type { Product } from "@/store/types";

/** Synthetic pill id representing "either bundle category" before a sub-filter narrows it. */
export const BUNDLE_PILL_ID = "BUNDLE";

export interface PillOption {
  id: string;
  label: string;
}

/** The primary pill row: All Products, one per real category, then the combined Bundle pill. */
export function buildPrimaryPills(): PillOption[] {
  return [
    { id: "ALL", label: "All Products" },
    ...CATEGORIES.map((id) => ({ id, label: categoryLabel(id) })),
    { id: BUNDLE_PILL_ID, label: "Bundles (GD & Impact)" },
  ];
}

/** The secondary "Bundle includes:" row, shown only when the Bundle pill is active. */
export function buildBundleSubPills(): PillOption[] {
  return [
    { id: "GDEXB", label: "General Dentistry" },
    { id: "IMPEXB", label: "Impact" },
  ];
}

/**
 * Whether a product matches the active pill selection.
 * - No active categories: everything matches.
 * - A plain category id (GD/WEB/DE/IMP) matches products of that exact category.
 * - The Bundle pill matches any BUNDLE_CATEGORIES product, narrowed by bundleFocus
 *   (GDEXB/IMPEXB) when at least one sub-pill is selected.
 */
export function matchesActiveFilters(
  product: Product,
  activeCategories: string[],
  bundleFocus: string[]
): boolean {
  if (activeCategories.length === 0) return true;

  return activeCategories.some((id) => {
    if (id === BUNDLE_PILL_ID) {
      if (!BUNDLE_CATEGORIES.includes(product.Category)) return false;
      if (bundleFocus.length === 0) return true;
      return bundleFocus.includes(product.Category);
    }
    return product.Category === id;
  });
}

/**
 * Whether a product passes the "My Purchases" filter: always true when the
 * filter is off; otherwise, true only for non-bundle products the member has
 * purchased (bundle products are excluded — they're repeat-purchase and never
 * show a "purchased" state elsewhere in the app).
 */
export function matchesPurchasedFilter(
  product: Product,
  purchasedOnly: boolean,
  purchasedCodes: Set<string>
): boolean {
  if (!purchasedOnly) return true;
  if (isBundleCategory(product.Category)) return false;
  return purchasedCodes.has(product.ProductCode);
}

/** Whether the "My Purchases" pill should be shown: at least one eligible (non-bundle, still-in-catalog) purchase. */
export function hasEligiblePurchases(purchasedCodes: Set<string>, products: Product[]): boolean {
  return products.some((p) => !isBundleCategory(p.Category) && purchasedCodes.has(p.ProductCode));
}
